import { fg } from '@confluence/feature-gating';

import type {
	AgentTrustReceipt,
	AgentTrustReceiptActor,
	AgentTrustReceiptScenario,
	AgentTrustReceiptScenarioId,
} from './agentTrustReceiptModel.ts';

const CREATED_BY_AI_LABEL = 'Generated with Rovo';
const EDITED_BY_AI_LABEL = 'Edited with Rovo';
const READ_BY_AI_LABEL = 'Viewed by Rovo';
const SOURCE_LABEL = 'Source';
const EVIDENCE_LABEL = 'Evidence';
const PERSISTENCE_LABEL = 'Persistence';
const NETWORK_CALLS_LABEL = 'Network calls';

const SPARK_RECEIPT_SCENARIOS: Record<AgentTrustReceiptScenarioId, AgentTrustReceiptScenario> = {
	CREATED_BY_AI: {
		id: 'CREATED_BY_AI',
		label: CREATED_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo generated this page.',
	},
	EDITED_BY_AI: {
		id: 'EDITED_BY_AI',
		label: EDITED_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo helped edit this page.',
	},
	READ_BY_AI: {
		id: 'READ_BY_AI',
		label: READ_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo read this page as context.',
	},
};

const SCENARIO_BY_BUCKET: AgentTrustReceiptScenarioId[] = [
	'CREATED_BY_AI',
	'EDITED_BY_AI',
	'READ_BY_AI',
];

const SPARK_ACTORS_BY_BUCKET: AgentTrustReceiptActor[] = [
	{
		id: 'rovo-agent-spark-preview',
		name: 'Rovo agent',
		type: 'agent',
		agentType: 'rovo',
		iconKey: 'rovo-agent',
	},
	{
		id: 'space-organizer-agent-spark-preview',
		name: 'Space Organizer Agent',
		type: 'agent',
		agentType: 'space-organizer',
		iconKey: 'space-organizer-agent',
	},
	{
		id: 'dev-docs-agent-spark-preview',
		name: 'Dev Docs Agent',
		type: 'agent',
		agentType: 'dev-docs',
		iconKey: 'dev-docs-agent',
	},
];

const isValidContentId = (contentId: string) => /^[1-9]\d*$/.test(contentId);

const resolveSparkBucket = (contentId: string): number => {
	const lastDigit = Number(contentId.charAt(contentId.length - 1));
	return lastDigit % SCENARIO_BY_BUCKET.length;
};

const createSparkPreviewSignals = (
	scenario: AgentTrustReceiptScenario,
): AgentTrustReceipt['signals'] => [
	{
		id: 'source',
		label: SOURCE_LABEL,
		value: 'Spark preview fixture',
	},
	{
		id: 'evidence',
		label: EVIDENCE_LABEL,
		value: `Example evidence for ${scenario.label.toLowerCase()}`,
	},
	{
		id: 'persistence',
		label: PERSISTENCE_LABEL,
		value: 'None',
	},
	{
		id: 'network',
		label: NETWORK_CALLS_LABEL,
		value: 'None',
	},
];

const createSparkPreviewEvents = (
	actor: AgentTrustReceiptActor,
	scenario: AgentTrustReceiptScenario,
): AgentTrustReceipt['events'] =>
	[
		{
			id: 'context-reviewed',
			occurredAt: '2026-06-03T21:00:00.000Z',
			title: 'Context reviewed',
			description: `${actor.name} inspected example page context for ${scenario.label.toLowerCase()}.`,
		},
		{
			id: 'agent-action',
			occurredAt: '2026-06-03T21:02:00.000Z',
			title: scenario.label,
			description: `${actor.name} performed the Spark preview activity recorded in this receipt.`,
		},
		{
			id: 'receipt-created',
			occurredAt: '2026-06-03T21:04:00.000Z',
			title: 'Receipt prepared',
			description: 'Example evidence was collected for review in this Spark preview receipt.',
		},
	].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

export const resolveAgentTrustReceipt = (contentId: string): AgentTrustReceipt | null => {
	if (!fg('cc_sparks_agent_trust_receipt')) {
		return null;
	}

	const normalizedContentId = contentId.trim();

	if (!isValidContentId(normalizedContentId)) {
		return null;
	}

	const sparkBucket = resolveSparkBucket(normalizedContentId);
	const scenario = SPARK_RECEIPT_SCENARIOS[SCENARIO_BY_BUCKET[sparkBucket]];
	const actor = SPARK_ACTORS_BY_BUCKET[sparkBucket];

	return {
		contentId: normalizedContentId,
		actor,
		scenario,
		summary: `${scenario.description} This receipt identifies ${actor.name}, the recorded agent activity, and the chronological events available for review. Spark preview data only, not a durable audit log.`,
		events: createSparkPreviewEvents(actor, scenario),
		signals: createSparkPreviewSignals(scenario),
	};
};
